import { useState, useCallback, useRef } from "react"

/**
 * Shared hook for two-way state binding in form control renderers.
 *
 * Resolves `element.bind` to the current state value, maintains a local copy
 * for immediate UI feedback (async flow dispatch would otherwise swallow
 * keystrokes), and syncs back when the external state changes.
 *
 * Uses ref-based external tracking instead of useState+useEffect to eliminate
 * the 2-render-cycle delay when StateManager changes external state.
 *
 * @param bind       - The bind path from element.bind (bare or #{}-wrapped)
 * @param resolve    - resolveSymbol from RenderContext
 * @param fallback   - Default value when bind is absent or resolves to nil
 * @param coerce     - Optional coercion (e.g. Boolean, Number, String)
 */
export function useBoundValue<T>(
  bind: string | undefined,
  resolve: (expr: string) => unknown,
  fallback: T,
  coerce?: (raw: unknown) => T,
): [T, (v: T) => void] {
  // Resolve the external (bound) value
  const raw = bind
    ? resolve(bind.startsWith("#{") ? bind : `#{${bind}}`)
    : undefined
  const external: T =
    raw != null ? (coerce ? coerce(raw) : (raw as T)) : fallback

  // Track previous external value to detect StateManager changes
  const prevExternalRef = useRef<T>(external)
  const [local, setLocal] = useState<T>(external)
  const isLocalOverrideRef = useRef(false)

  // Detect external change DURING render (synchronous, no useEffect delay)
  let value: T
  if (prevExternalRef.current !== external) {
    // External (StateManager) changed — adopt immediately, clear local override
    prevExternalRef.current = external
    value = external
    // Reset local state to match (for next render's baseline)
    if (local !== external) {
      setLocal(external)
    }
    isLocalOverrideRef.current = false
  } else if (isLocalOverrideRef.current) {
    // User typed / interacted — use local value
    value = local
  } else {
    // No change — use external (single source of truth)
    value = external
  }

  const update = useCallback((v: T) => {
    isLocalOverrideRef.current = true
    setLocal(v)
  }, [])

  return [value, update]
}
